import { useMemo, useState } from 'react'

import { Button, CircularProgress, TextField, Typography, makeStyles } from '@material-ui/core'
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'

import api from 'common/api'
import { type ConfirmResponse } from 'common/auth'
import { clearLocalStorage, routeToApp, setCookies } from 'common/login-service'
import { passwordHelperText, passwordYupShape } from 'common/password-validation'
import { usePasswordRequirements } from 'common/usePasswordRequirements'
import Title from 'components/Title'

const useStyles = makeStyles((theme) => ({
  resetForm: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  continueButton: {
    marginTop: theme.spacing(2),
  },
}))

type ResetPasswordFormValues = {
  password: {
    pass: string
    passConfirm: string
  }
}

type ResetPasswordProps = {
  userId?: string
  confirmCode?: string
}

const ResetPassword = ({ userId, confirmCode }: ResetPasswordProps) => {
  const classes = useStyles()
  const [reset, setReset] = useState(false)
  const [response, setResponse] = useState<ConfirmResponse>()
  const [error, setError] = useState(false)

  const { minLength, loading, error: requirementsError } = usePasswordRequirements(userId, confirmCode)

  const yupValidation = useMemo(
    () =>
      Yup.object().shape({
        password: Yup.object().shape(passwordYupShape(minLength)),
      }),
    [minLength],
  )

  const handleSubmit = (values: ResetPasswordFormValues, props: FormikHelpers<ResetPasswordFormValues>) => {
    props.setSubmitting(true)
    const body = {
      Id: userId,
      Password: {
        Pass: values.password.pass,
        PassConfirm: values.password.passConfirm,
      },
      resetCode: confirmCode,
    }
    api
      .post<unknown, ConfirmResponse>('/api/user/password/reset', body)
      .then((res) => {
        setResponse(res)
        setReset(true)
      })
      .catch(() => {
        setError(true)
        props.setSubmitting(false)
      })
  }

  const handleLogin = () => {
    const userType = response?.UserTypes[0]
    if (!userType) {
      console.warn('No user type returned on password reset, cannot log in automatically')
      return
    }
    clearLocalStorage(userType)
    setCookies(userType, response?.access_token)
    routeToApp(userType)
  }

  const initialValue: ResetPasswordFormValues = {
    password: {
      pass: '',
      passConfirm: '',
    },
  }

  if (loading) {
    return <CircularProgress />
  }

  if (requirementsError === 'invalid') {
    return (
      <>
        <Title title="Reset link expired" />
        <Typography>
          This password reset link is invalid or has expired. Please{' '}
          <Link to="/forgot-password">request a new one</Link>.
        </Typography>
      </>
    )
  }

  if (reset) {
    return (
      <>
        <Title title="Your password has been reset ✅" />
        <Button
          className={classes.continueButton}
          variant="contained"
          color="primary"
          onClick={handleLogin}
        >
          Log In
        </Button>
      </>
    )
  }

  return (
    <>
      <Title title="Set a new password 🔑" />
      <Formik
        initialValues={initialValue}
        validateOnMount
        validationSchema={yupValidation}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, isSubmitting }) => (
          <Form
            className={classes.resetForm}
            name="resetPassword"
          >
            <Field
              type="password"
              id="pass"
              name="password.pass"
              as={TextField}
              required
              fullWidth
              variant="outlined"
              label="Password"
              helperText={
                errors.password?.pass && touched.password?.pass ? (
                  <ErrorMessage name="password.pass" />
                ) : (
                  passwordHelperText(minLength)
                )
              }
              error={errors.password?.pass && touched.password?.pass}
            />
            <Field
              type="password"
              id="passConfirm"
              name="password.passConfirm"
              as={TextField}
              required
              fullWidth
              variant="outlined"
              label="Confirm Password"
              helperText={<ErrorMessage name="password.passConfirm" />}
              error={errors.password?.passConfirm && touched.password?.passConfirm}
            />
            {error && (
              <Typography
                role="alert"
                color="error"
              >
                An unexpected error occurred, please try again later.
              </Typography>
            )}
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!isValid || isSubmitting}
            >
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default ResetPassword
